import React, { type MouseEventHandler } from 'react';
import { Field, Formik, type FormikHelpers } from 'formik';
import { object as yupObject } from 'yup';
import Box from '@mui/material/Box';
import clsx from 'clsx';
import { ReCaptcha, type PaymentType, type User } from '@elevator/ui';
import type { CampaignCheckoutValues } from 'types/campaignCheckout';
import {
  getStripeBillingInitialValues,
  recaptchaValidationSchema,
  stripeBillingValidationSchema,
} from 'lib/checkoutValidation';
import { PageHeader } from 'molecules';
import CampaignCheckoutFields from './CampaignCheckoutFields';
import CampaignCheckoutActions from './CampaignCheckoutActions';
import useStyles from './styles';

export type CampaignCheckoutStepProps = {
  currentUser: User | null;
  handleSubmit: (
    values: CampaignCheckoutValues,
    helpers: FormikHelpers<CampaignCheckoutValues>,
    paymentType: PaymentType,
    paypalOrderId?: string | null,
    bitPayInvoiceId?: string | null,
  ) => void;
  handleBackClick: MouseEventHandler;
  totalCost: number;
  typeId: string;
  onBeforeAuthRedirect?: () => void | Promise<void>;
};

const CheckoutStep = ({
  currentUser,
  handleSubmit,
  handleBackClick,
  totalCost,
  typeId,
  onBeforeAuthRedirect,
}: CampaignCheckoutStepProps) => {
  const { classes } = useStyles();
  const [paymentType, setPaymentType] = React.useState<PaymentType>('Stripe');
  const [paypalOrderId, setPaypalOrderId] = React.useState<string | null>(null);
  const [bitPayInvoiceId, setBitPayInvoiceId] = React.useState<string | null>(null);
  const paypalOrderIdRef = React.useRef<string | null>(null);

  const checkoutValidationSchema = React.useMemo(() => {
    if (!currentUser) {
      return yupObject({});
    }

    return yupObject({
      ...(paymentType === 'Stripe' && stripeBillingValidationSchema),
      ...(paymentType === 'Stripe' && recaptchaValidationSchema),
    });
  }, [currentUser, paymentType]);

  const checkoutInitialValues = React.useMemo((): CampaignCheckoutValues => {
    return {
      ...(paymentType === 'Stripe' && getStripeBillingInitialValues()),
    };
  }, [paymentType]);

  return (
    <Box className={classes.stepRoot} data-component="CampaignCheckoutStep">
      <Formik
        enableReinitialize
        initialValues={checkoutInitialValues}
        validationSchema={checkoutValidationSchema}
        validateOnChange
        onSubmit={(values, helpers) => {
          const finalPaypalOrderId = paypalOrderIdRef.current || paypalOrderId;
          paypalOrderIdRef.current = null;
          handleSubmit(values, helpers, paymentType, finalPaypalOrderId, bitPayInvoiceId);
        }}
        validateOnMount={paymentType === 'PayPal' && currentUser ? true : false}
      >
        {(formikProps) => (
          <Box className={classes.stepFormColumn}>
            <Box
              className={clsx(classes.stepMain, !currentUser && classes.stepMainGuest)}
            >
              <PageHeader title="Checkout" divider />
              <CampaignCheckoutFields
                {...formikProps}
                totalCost={totalCost}
                paymentType={paymentType}
                setPaymentType={setPaymentType}
                typeId={typeId}
                currentUser={currentUser}
                onBeforeAuthRedirect={onBeforeAuthRedirect}
              />
            </Box>
            {currentUser && paymentType === 'Stripe' && (
              <Box className={classes.captchaSection}>
                <Field
                  component={ReCaptcha}
                  googleRecaptchaKey={process.env.GOOGLE_RECAPTCHA_SITE_KEY}
                  autoTheme={true}
                />
              </Box>
            )}
            <Box className={classes.stepActionsFooter}>
              <CampaignCheckoutActions
                currentUser={currentUser}
                handleBackClick={handleBackClick}
                paymentType={paymentType}
                setPaypalOrderId={setPaypalOrderId}
                paypalOrderIdRef={paypalOrderIdRef}
                totalCost={totalCost}
                typeId={typeId}
              />
            </Box>
          </Box>
        )}
      </Formik>
    </Box>
  );
};

export default CheckoutStep;
